import { Button, IconButton } from '@assembly-js/design-system';
import clsx from 'clsx';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PortalConfigContext } from 'src/context';
import { useAppDispatch } from 'src/hooks/useStore';
import { useGetClientPreviewSummaryQuery } from 'src/services/api/improvedOnboardingApi';
import { setStudioClientPreview } from 'src/store/ui/actions';
import type { ClientPreviewSteps } from 'src/store/ui/types';

import {
  getDashboardOrigin,
  switchToInternalPortalTab,
} from './internalPortalTab';

interface ClientActivationChecklistProps {
  /** The owner's current checklist, when the preview link carried it. */
  linkSteps: ClientPreviewSteps | null;
  backgroundColor: string;
  sidebarColor: string;
  textColor: string;
}

/**
 * The onboarding-activation Getting started card in the client portal preview: the
 * owner's current checklist as "x/y steps completed", one bar segment per step, and
 * a way back to the internal view. Collapsed, it's a 48px pill with the count, in the
 * portal's sidebar colors and with no hover effect.
 */
export const ClientActivationChecklist = ({
  linkSteps,
  backgroundColor,
  sidebarColor,
  textColor,
}: ClientActivationChecklistProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { brand, theme, isStudioUser } = useContext(PortalConfigContext);
  const [isCollapsed, setIsCollapsed] = useState(false);
  // The activation email's link is minted at signup and can't carry progress, so
  // the test client asks for it instead.
  const { data: summary } = useGetClientPreviewSummaryQuery(undefined, {
    skip: linkSteps !== null || !isStudioUser,
  });
  const steps = linkSteps ?? summary;

  // Every step done means the owner has finished, so there's nothing left to show.
  if (!steps || steps.completed >= steps.total) {
    return null;
  }

  const title = t(($) => $.page_apps.app_builder.getting_started.title);
  // The button uses the portal's configured accent color, falling back to the
  // portal primary when no accent is set.
  const accentColor = brand.clientAccentColor || theme.palette.primary.main;

  const handleBackToInternalView = () => {
    // Leaving ends the preview, so the card hides for the rest of the session.
    dispatch(setStudioClientPreview(false));
    if (!switchToInternalPortalTab()) {
      window.open(
        getDashboardOrigin(window.location),
        '_blank',
        'noopener,noreferrer',
      );
    }
  };

  if (isCollapsed) {
    return (
      <button
        type="button"
        aria-expanded={false}
        aria-label={t(
          ($) => $.page_apps.app_builder.getting_started.expand_checklist,
        )}
        onClick={() => setIsCollapsed(false)}
        className="mb-1 box-border flex h-12 w-full cursor-pointer appearance-none items-center gap-2 rounded-lg border-0 py-0 pl-3 pr-3.5"
        style={{ backgroundColor, color: textColor }}
      >
        <span className="min-w-0 flex-1 truncate text-left text-[13px] leading-[18px]">
          {title}
        </span>
        <span className="shrink-0 text-[12px] leading-4 tabular-nums opacity-70">
          {t(($) => $.page_apps.app_builder.getting_started.steps_count, {
            completed: steps.completed,
            total: steps.total,
          })}
        </span>
      </button>
    );
  }

  return (
    <div
      className="mb-1 box-border w-full rounded-lg px-3 pb-3 pt-3"
      style={{ backgroundColor, color: textColor }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="text-[13px] leading-[18px]">{title}</span>
          <span className="text-[12px] leading-4 tabular-nums opacity-70">
            {t(($) => $.page_apps.app_builder.getting_started.steps_completed, {
              completed: steps.completed,
              total: steps.total,
            })}
          </span>
        </div>
        <IconButton
          icon="ChevronDown"
          variant="minimal"
          size="sm"
          aria-expanded
          aria-label={t(
            ($) => $.page_apps.app_builder.getting_started.collapse_checklist,
          )}
          onClick={() => setIsCollapsed(true)}
          className="-mr-1 size-5! shrink-0 border-0! bg-transparent! p-0! [&_svg]:size-3!"
          style={{ color: textColor }}
        />
      </div>
      <div className="mb-3 mt-2.5 flex gap-1" aria-hidden="true">
        {Array.from({ length: steps.total }, (_, index) => (
          <div
            key={index}
            className="h-[3px] flex-1 overflow-hidden rounded-full"
            style={{ backgroundColor: sidebarColor }}
          >
            <div
              className={clsx(
                'h-full rounded-full transition-[width] duration-500 ease-out',
                index < steps.completed ? 'w-full' : 'w-0',
              )}
              style={{ backgroundColor: textColor }}
            />
          </div>
        ))}
      </div>
      <Button
        label={t(
          ($) => $.page_apps.app_builder.getting_started.back_to_internal_view,
        )}
        variant="primary"
        size="sm"
        className="h-7 w-full"
        style={{
          backgroundColor: textColor,
          borderColor: accentColor,
          color: accentColor,
        }}
        onClick={handleBackToInternalView}
      />
    </div>
  );
};
